/**
 * The only place the console formats dates (REQ-05-019). Operators work in
 * Vienna; timestamps from the API are UTC.
 */
const LOCALE = 'de-AT';
const TIME_ZONE = 'Europe/Vienna';

/** `26.09.2026, 16:30` for `2026-09-26T14:30:00Z`. */
export function formatDateTime(isoUtc: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIME_ZONE,
  }).format(new Date(isoUtc));
}

/** `1.234` for 1234. */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat(LOCALE).format(value);
}

/** `0,26 $` for `{amount: "0.260001", currency: "USD"}`; the decimal string is formatted as is, never parsed into a float. */
export function formatMoney(money: { readonly amount: string; readonly currency: string }, fractionDigits = 2): string {
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: money.currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(money.amount as Intl.StringNumericLiteral);
}

/** `Oktober 2026` for the UTC month `2026-10`. */
export function formatMonth(month: string): string {
  return new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${month}-01T00:00:00Z`));
}

/** `20.10.2026` for a UTC timestamp, in Vienna. */
export function formatDate(isoUtc: string): string {
  return new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: TIME_ZONE }).format(new Date(isoUtc));
}

/** `1,2 GB` for 1_200_000_000 bytes (decimal units, like the web app's storage figures). */
export function formatBytes(bytes: number): string {
  const units = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'] as const;
  let value = bytes;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  return new Intl.NumberFormat(LOCALE, { style: 'unit', unit: units[unit], unitDisplay: 'short', maximumFractionDigits: unit === 0 ? 0 : 1 }).format(value);
}

/** `Okt. 26` for the UTC month `2026-10` (chart axes). */
export function formatMonthShort(month: string): string {
  return new Intl.DateTimeFormat(LOCALE, { month: 'short', year: '2-digit', timeZone: 'UTC' }).format(new Date(`${month}-01T00:00:00Z`));
}

/** `20.10.` for the UTC day `2026-10-20` (chart axes). */
export function formatDayShort(day: string): string {
  return new Intl.DateTimeFormat(LOCALE, { day: '2-digit', month: '2-digit', timeZone: 'UTC' }).format(new Date(`${day}T00:00:00Z`));
}

/** `3,4 %` for 0.034. */
export function formatPercent(ratio: number): string {
  return new Intl.NumberFormat(LOCALE, { style: 'percent', maximumFractionDigits: 1 }).format(ratio);
}

/** The Vienna day (`2026-10-20`) of a UTC timestamp, as the date picker takes it. */
export function viennaDay(isoUtc: string): string {
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: TIME_ZONE }).format(new Date(isoUtc));
}

/** The UTC instant of 23:59:59 in Vienna on `day` (`2026-10-20`), for an expiry or trial end picked as a date. */
export function endOfViennaDay(day: string): string {
  const guess = Date.parse(`${day}T23:59:59Z`);
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone: TIME_ZONE, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
      .formatToParts(new Date(guess))
      .map((part) => [part.type, part.value]),
  );
  const offset = Date.UTC(+parts.year!, +parts.month! - 1, +parts.day!, +parts.hour!, +parts.minute!, +parts.second!) - guess;
  return new Date(guess - offset).toISOString();
}
